import { Cartesian3, Cartographic, TerrainProvider } from 'cesium';
import { config } from '../app/config';
import { geodeticUp } from '../geo/enuFrame';
import { resampleByArcLength, smoothHeights, type LonLat } from '../geo/resample';
import { sampleHeights } from '../geo/terrainSample';
import type { TrackStation } from './types';

/**
 * Turn a raw centreline into rail-level TrackStations with per-station local
 * frames. Resamples to sleeper pitch, drapes onto terrain, lifts to rail level,
 * smooths the grade, then computes tangent/up/right at each station.
 */
export async function buildStations(
  line: ReadonlyArray<LonLat>,
  terrain: TerrainProvider,
): Promise<TrackStation[]> {
  const sampled = resampleByArcLength(line, config.track.sleeperPitch);
  if (sampled.length < 2) return [];

  // Drape: sample ground height at each station.
  const cartos = sampled.map(([lon, lat]) => Cartographic.fromDegrees(lon, lat));
  await sampleHeights(terrain, cartos);

  // Lift to rail level and smooth the grade.
  const railHeights = smoothHeights(
    cartos.map((c) => (c.height ?? 0) + config.track.railHeightAboveGround),
  );

  const positions = sampled.map(([lon, lat], i) =>
    Cartesian3.fromDegrees(lon, lat, railHeights[i]),
  );

  // Build frames: tangent via central difference, up = geodetic normal,
  // right = tangent × up.
  const stations: TrackStation[] = [];
  let distance = 0;
  for (let i = 0; i < positions.length; i++) {
    const prev = positions[Math.max(0, i - 1)];
    const next = positions[Math.min(positions.length - 1, i + 1)];

    const tangent = Cartesian3.normalize(
      Cartesian3.subtract(next, prev, new Cartesian3()),
      new Cartesian3(),
    );
    const up = geodeticUp(positions[i]);
    const right = Cartesian3.normalize(
      Cartesian3.cross(tangent, up, new Cartesian3()),
      new Cartesian3(),
    );

    if (i > 0) distance += Cartesian3.distance(positions[i - 1], positions[i]);
    stations.push({ position: positions[i], tangent, up, right, distance });
  }

  return stations;
}
